/*
 * HomeFixer360 - Cookie Policy
 * Copyright (c) 2025 Guy T. Dunn Sr. / Idea Guy Brands Incorporated
 * All Rights Reserved.
 * 
 * This software is proprietary and confidential. Unauthorized copying, distribution,
 * modification, or use is strictly prohibited.
 * 
 * For licensing inquiries: guy@ideaguybrands.com
 */

import React, { useState } from 'react';
import { ArrowLeft, Cookie, Settings, Shield, BarChart3, UserCheck } from 'lucide-react';
import { Button } from '../components/ui/button';

const CookiePolicy = () => {
  const [cookieSettings, setCookieSettings] = useState({
    essential: true, // Always required
    functional: true,
    analytics: false,
    marketing: false
  });

  const handleCookieSetting = (type, value) => {
    if (type !== 'essential') { // Essential cookies can't be disabled
      setCookieSettings(prev => ({ ...prev, [type]: value }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center space-x-4">
            <Button 
              variant="outline" 
              onClick={() => window.history.back()}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Cookie Policy</h1>
              <p className="text-gray-600">How we use cookies and tracking technologies</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-sm border p-8">
          
          {/* Last Updated */}
          <div className="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center space-x-2">
              <Cookie className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-blue-900">Last Updated: January 1, 2025</span>
            </div>
            <p className="text-blue-700 mt-2">This Cookie Policy explains how HomeFixer360 uses cookies and similar technologies.</p>
          </div>

          {/* What Are Cookies */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Cookie className="w-6 h-6 text-orange-600 mr-3" />
              What Are Cookies?
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Cookies are small text files that are placed on your device when you visit our website. They help us provide you 
              with a better experience by remembering your preferences, keeping you logged in, and analyzing how our platform is used.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We also use similar technologies like web beacons, pixels, and local storage to collect information about your 
              interaction with HomeFixer360.
            </p>
          </section>

          {/* Cookie Settings Panel */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Settings className="w-5 h-5 text-purple-600 mr-2" />
              Manage Your Cookie Preferences
            </h2>
            <p className="text-gray-700 mb-6">
              You can control which cookies we use by adjusting your preferences below. Note that disabling certain 
              cookies may affect the functionality of HomeFixer360.
            </p>

            <div className="space-y-4">
              {/* Essential Cookies */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    <h3 className="font-semibold text-gray-900">Essential Cookies</h3>
                  </div>
                  <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                    Always Active
                  </div>
                </div>
                <p className="text-gray-600 text-sm">
                  These cookies are necessary for the platform to function and cannot be disabled. They include 
                  authentication, security, and basic functionality cookies.
                </p>
              </div>

              {/* Functional Cookies */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Settings className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-900">Functional Cookies</h3>
                  </div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={cookieSettings.functional}
                      onChange={(e) => handleCookieSetting('functional', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">Enable</span>
                  </label>
                </div>
                <p className="text-gray-600 text-sm">
                  These cookies remember your preferences and settings, such as custom pricing rates, 
                  measurement units, and interface preferences.
                </p>
              </div>

              {/* Analytics Cookies */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-gray-900">Analytics Cookies</h3>
                  </div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={cookieSettings.analytics}
                      onChange={(e) => handleCookieSetting('analytics', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">Enable</span>
                  </label>
                </div>
                <p className="text-gray-600 text-sm">
                  These cookies help us understand how you use HomeFixer360, which features are most popular, 
                  and how we can improve the platform.
                </p>
              </div>

              {/* Marketing Cookies */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <UserCheck className="w-5 h-5 text-orange-600" />
                    <h3 className="font-semibold text-gray-900">Marketing Cookies</h3>
                  </div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={cookieSettings.marketing}
                      onChange={(e) => handleCookieSetting('marketing', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">Enable</span>
                  </label>
                </div>
                <p className="text-gray-600 text-sm">
                  These cookies are used to deliver relevant advertisements and measure the effectiveness 
                  of our marketing campaigns.
                </p>
              </div>
            </div>

            <div className="mt-6 flex gap-4">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                Save Preferences
              </Button>
              <Button variant="outline" onClick={() => setCookieSettings({...cookieSettings, functional: false, analytics: false, marketing: false})}>
                Reject All (except Essential)
              </Button>
              <Button variant="outline" onClick={() => setCookieSettings({...cookieSettings, functional: true, analytics: true, marketing: true})}>
                Accept All
              </Button>
            </div>
          </section>

          {/* Types of Cookies We Use */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Types of Cookies We Use</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <Shield className="w-5 h-5 text-green-600 mr-2" />
                  Essential Cookies
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border-b px-4 py-2 text-left font-medium">Cookie Name</th>
                        <th className="border-b px-4 py-2 text-left font-medium">Purpose</th>
                        <th className="border-b px-4 py-2 text-left font-medium">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border-b px-4 py-2 font-mono">auth_token</td>
                        <td className="border-b px-4 py-2">Keeps you logged in securely</td>
                        <td className="border-b px-4 py-2">Session</td>
                      </tr>
                      <tr>
                        <td className="border-b px-4 py-2 font-mono">csrf_token</td>
                        <td className="border-b px-4 py-2">Prevents cross-site request forgery attacks</td>
                        <td className="border-b px-4 py-2">Session</td>
                      </tr>
                      <tr>
                        <td className="border-b px-4 py-2 font-mono">session_id</td>
                        <td className="border-b px-4 py-2">Maintains your session state</td>
                        <td className="border-b px-4 py-2">24 hours</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 font-mono">security_check</td>
                        <td className="px-4 py-2">Validates security headers and requests</td>
                        <td className="px-4 py-2">Session</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <Settings className="w-5 h-5 text-blue-600 mr-2" />
                  Functional Cookies
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border-b px-4 py-2 text-left font-medium">Cookie Name</th>
                        <th className="border-b px-4 py-2 text-left font-medium">Purpose</th>
                        <th className="border-b px-4 py-2 text-left font-medium">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border-b px-4 py-2 font-mono">user_preferences</td>
                        <td className="border-b px-4 py-2">Stores your custom pricing rates and settings</td>
                        <td className="border-b px-4 py-2">1 year</td>
                      </tr>
                      <tr>
                        <td className="border-b px-4 py-2 font-mono">ui_theme</td>
                        <td className="border-b px-4 py-2">Remembers your interface preferences</td>
                        <td className="border-b px-4 py-2">6 months</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 font-mono">measurement_units</td>
                        <td className="px-4 py-2">Saves your preferred measurement units</td>
                        <td className="px-4 py-2">1 year</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <BarChart3 className="w-5 h-5 text-purple-600 mr-2" />
                  Analytics Cookies
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border-b px-4 py-2 text-left font-medium">Cookie Name</th>
                        <th className="border-b px-4 py-2 text-left font-medium">Purpose</th>
                        <th className="border-b px-4 py-2 text-left font-medium">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border-b px-4 py-2 font-mono">_ga</td>
                        <td className="border-b px-4 py-2">Google Analytics - distinguishes users</td>
                        <td className="border-b px-4 py-2">2 years</td>
                      </tr>
                      <tr>
                        <td className="border-b px-4 py-2 font-mono">_ga_*</td>
                        <td className="border-b px-4 py-2">Google Analytics - session information</td>
                        <td className="border-b px-4 py-2">2 years</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 font-mono">usage_stats</td>
                        <td className="px-4 py-2">Tracks feature usage for platform improvement</td>
                        <td className="px-4 py-2">30 days</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>

          {/* Third-Party Cookies */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Third-Party Cookies and Services</h2>
            <p className="text-gray-700 mb-4">
              HomeFixer360 integrates with trusted third-party services that may set their own cookies:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Stripe:</strong> Payment processing - essential for billing and subscription management</li>
              <li><strong>Google Maps:</strong> Satellite imagery - required for roof measurement functionality</li>
              <li><strong>Google Analytics:</strong> Usage analytics - helps us improve the platform (optional)</li>
              <li><strong>Supabase:</strong> Database and authentication - essential for platform operations</li>
              <li><strong>SendGrid:</strong> Email services - for notifications and customer communication</li>
            </ul>
            <p className="text-gray-700 mt-4">
              These services have their own privacy policies and cookie practices. We recommend reviewing their 
              policies to understand how they handle your data.
            </p>
          </section>

          {/* Browser Controls */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Browser Cookie Controls</h2>
            <p className="text-gray-700 mb-4">
              You can control cookies through your browser settings. However, please note that disabling cookies 
              may affect the functionality of HomeFixer360.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Popular Browser Settings:</h3>
                <ul className="space-y-1 text-gray-700 text-sm">
                  <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies</li>
                  <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies</li>
                  <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
                  <li><strong>Edge:</strong> Settings → Site Permissions → Cookies</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Mobile Browser Settings:</h3>
                <ul className="space-y-1 text-gray-700 text-sm">
                  <li><strong>iOS Safari:</strong> Settings → Safari → Block All Cookies</li>
                  <li><strong>Android Chrome:</strong> Chrome → Settings → Site Settings → Cookies</li>
                  <li><strong>Samsung Internet:</strong> Menu → Settings → Sites and downloads</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Impact of Disabling Cookies */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Impact of Disabling Cookies</h2>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-gray-700 mb-4">
                <strong>Important:</strong> Disabling certain cookies may significantly impact your HomeFixer360 experience:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 text-sm">
                <li>You may need to log in repeatedly</li>
                <li>Custom pricing settings won't be saved</li>
                <li>Platform preferences will reset each session</li>
                <li>Some measurement features may not function properly</li>
                <li>Payment processing may be affected</li>
              </ul>
            </div>
          </section>

          {/* Updates to Cookie Policy */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Updates to This Cookie Policy</h2>
            <p className="text-gray-700">
              We may update this Cookie Policy periodically to reflect changes in our practices, technology, 
              or legal requirements. When we make significant changes, we will notify you by email and by 
              posting a notice on our platform. Your continued use of HomeFixer360 after changes take 
              effect constitutes acceptance of the updated Cookie Policy.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-8 p-6 bg-gray-50 rounded-lg">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Questions About Cookies</h2>
            <p className="text-gray-700 mb-4">
              If you have questions about our use of cookies or this Cookie Policy, please contact:
            </p>
            <div className="space-y-2 text-gray-700">
              <p><strong>Privacy Officer:</strong> Guy T. Dunn Sr.</p>
              <p><strong>Company:</strong> Idea Guy Brands Incorporated</p>
              <p><strong>Email:</strong> guy@ideaguybrands.com</p>
              <p><strong>Subject Line:</strong> "Cookie Policy Inquiry"</p>
            </div>
          </section>

          {/* Copyright Footer */}
          <div className="border-t pt-6 mt-8 text-center text-gray-500 text-sm">
            <p>© 2025 Guy T. Dunn Sr. / Idea Guy Brands Incorporated. All Rights Reserved.</p>
            <p>HomeFixer360™ is a trademark of Idea Guy Brands Incorporated.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;